import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import {
  FileText,
  User,
  BadgeCheck,
  Calendar,
  CalendarCheck,
  AlignLeft,
} from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import type { Matter } from '../types';

interface MatterOverviewPanelProps {
  matter: Matter;
}

function DetailItem({
  icon: Icon,
  label,
  children,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-start gap-3">
      <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-50">
        <Icon className="h-4 w-4 text-slate-400" />
      </div>
      <div className="min-w-0">
        <p className="text-xs font-medium uppercase tracking-wide text-slate-400">
          {label}
        </p>
        <div className="mt-0.5 text-sm font-medium text-slate-900">{children}</div>
      </div>
    </div>
  );
}

/**
 * Case 360° (Stage 14 redesign) — the Overview tab, essentials only:
 * description + who/what/when (client, contract, practice area, opened/closed).
 * Everything else lives where it belongs: contact details on the client page,
 * contract terms on the contract page, lifecycle in the header stepper.
 */
export function MatterOverviewPanel({ matter }: MatterOverviewPanelProps) {
  const { t } = useTranslation();

  return (
    <Card className="bg-white border-slate-200 shadow-sm">
      <CardHeader className="pb-3 border-b border-slate-100">
        <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
          <AlignLeft className="h-5 w-5 text-slate-400" />
          {t('matterOverview')}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-5 space-y-6">
        {/* What the matter is about */}
        {matter?.description && (
          <div>
            <p className="mb-1.5 text-xs font-medium uppercase tracking-wide text-slate-400">
              {t('description')}
            </p>
            <p className="whitespace-pre-line text-sm leading-relaxed text-slate-700">
              {matter.description}
            </p>
          </div>
        )}

        {/* The essentials grid */}
        <div className="grid grid-cols-1 gap-x-8 gap-y-5 sm:grid-cols-2 lg:grid-cols-3">
          {matter?.client && (
            <DetailItem icon={User} label={t('client')}>
              <Link
                to={`/app/clients/${matter.client.id}`}
                className="text-blue-600 hover:underline"
              >
                {/* Full client name — same shape as the matters list columns
                    (persons: name + lastname + middlename; companies: name). */}
                {[matter.client?.name, matter.client?.lastname, matter.client?.middlename]
                  .filter(Boolean)
                  .join(' ') || '—'}
              </Link>
            </DetailItem>
          )}

          {matter?.contract && (
            <DetailItem icon={FileText} label={t('contract')}>
              <Link
                to={`/app/contracts/${matter.contract.id}`}
                className="text-blue-600 hover:underline"
              >
                {matter.contract?.name || '—'}
              </Link>
            </DetailItem>
          )}

          {matter?.practiceArea && (
            <DetailItem icon={BadgeCheck} label={t('practiceArea')}>
              {matter.practiceArea?.name}
            </DetailItem>
          )}

          <DetailItem icon={Calendar} label={t('matterDetail.overview.opened')}>
            {matter?.createdAt
              ? format(new Date(matter.createdAt), 'MMM d, yyyy')
              : '—'}
          </DetailItem>

          {matter?.endedAt && (
            <DetailItem icon={CalendarCheck} label={t('matterDetail.overview.closed')}>
              {format(new Date(matter.endedAt), 'MMM d, yyyy')}
            </DetailItem>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
